import React, { useCallback, useEffect, useState } from 'react'
import {
	Box,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
	useMediaQuery,
} from '@mui/material'
import axios from 'axios'
import { getFromStorage } from '../utils/storage'
import { DOMAIN } from '../utils/api'
import { handleError } from '../utils/error'
import UserLayout from '../layouts/UserLayout'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import PickDateWithWeek from '../components/PickDateWithWeek'
import PersonIcon from '@mui/icons-material/Person'
import { currentUserActions } from '../redux/store'
import { borderFormat, mainGreen, mainRed } from '../utils/color'

export default function Statistics() {
	const { storeId } = useParams()
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const mediaMd = useMediaQuery('(min-width:768px)')
	const min348 = useMediaQuery('(min-width:348px)')
	const { currentUser } = useSelector((state) => state.currentUser)
	const [statistics, setStatistics] = useState([])
	const [userStatistics, setUserStatistics] = useState([])
	const [loadingStatistics, setLoadingStatistics] = useState(true)
	const token = getFromStorage('current-user')?.accessToken
	// Mirrors the backend gates: seeRevenue unlocks both the personal and the
	// per-store revenue tables, alongside the legacy scopes.
	const permission = currentUser.permission || []
	const canSeePersonal = permission.includes('seePersonalStatistics') || permission.includes('seeRevenue')
	const canSeeStore = permission.includes('seeStatisticsByStore') || permission.includes('seeRevenue')
	const [timePick, setTimePick] = useState({
		shift: '',
		date: new Date().getDate()?.toString(),
		month: (new Date().getMonth() + 1)?.toString(),
		year: new Date().getFullYear()?.toString(),
	})
	const setTime = (time) => {
		setTimePick(time)
	}

	const userStatisticsApiURL = useCallback(() => {
		const { shift, date, month, year } = timePick
		if (currentUser.role === 'admin' || currentUser.role === 'director' || currentUser.role === 'assistant') {
			return `${DOMAIN}/admin/users/store/statistics/${storeId}?shift=${shift}&date=${date}&month=${month}&year=${year}`
		} else {
			return `${DOMAIN}/users/store/statistics?shift=${shift}&date=${date}&month=${month}&year=${year}`
		}
	}, [currentUser.role, timePick, storeId])

	const getStatistics = useCallback(
		async function () {
			setLoadingStatistics(true)
			const { shift, date, month, year } = timePick
			try {
				const response = await axios({
					url: `${DOMAIN}/user/statistics?shift=${shift}&date=${date}&month=${month}&year=${year}`,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'GET',
					withCredentials: true,
				})
				setStatistics(response.data)
				setLoadingStatistics(false)
			} catch (error) {
				handleError(error)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[token, timePick, navigate, dispatch],
	)

	useEffect(() => {
		if (canSeePersonal) {
			getStatistics()
		}
	}, [getStatistics, canSeePersonal])

	const getUserStatistics = useCallback(
		async function () {
			const url = userStatisticsApiURL()
			setLoadingStatistics(true)
			try {
				const response = await axios({
					url,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'GET',
					withCredentials: true,
				})
				setUserStatistics(response.data)
				setLoadingStatistics(false)
			} catch (error) {
				handleError(error)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[token, navigate, dispatch, userStatisticsApiURL],
	)

	useEffect(() => {
		if (canSeeStore) {
			getUserStatistics()
		}
	}, [getUserStatistics, canSeeStore])

	return (
		<UserLayout>
			<Box sx={{ margin: '0 auto' }}>
				<Box
					sx={{
						padding: '10px 10px 10px',
						background: '#fff',
					}}>
					<Box sx={{ textAlign: 'center' }}>
						<Typography sx={{ fontWeight: 'bold' }}>THỐNG KÊ DOANH THU</Typography>
					</Box>
					<PickDateWithWeek setTime={setTime} time={timePick} />
				</Box>
				<Box
					overflow='auto'
					sx={{
						margin: '0 auto',
						padding: '10px',
						position: 'fixed',
						top: min348 ? 170 : 200,
						bottom: 0,
						left: mediaMd ? 300 : 0,
						right: 0,
						borderLeft: mediaMd ? borderFormat : 'none',
					}}>
					{!loadingStatistics && canSeePersonal && (
						<Table sx={{ mb: 3, minWidth: 700, background: '#fff' }}>
							<TableHead sx={{ background: mainGreen, border: 'none' }}>
								<TableRow>
									<TableCell
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
											minWidth: 100,
										}}>
										<PersonIcon viewBox='0 -3 24 24' />
									</TableCell>
									<TableCell
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
											minWidth: 100,
										}}>
										Doanh thu (đ)
									</TableCell>
									<TableCell
										colSpan='2'
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
											minWidth: 150,
										}}>
										Đơn CK (đ)
									</TableCell>
									<TableCell
										colSpan='2'
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
											minWidth: 150,
										}}>
										Đơn App (đ)
									</TableCell>
									<TableCell
										colSpan='2'
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
											minWidth: 150,
										}}>
										Đơn TM (đ)
									</TableCell>
									<TableCell
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
											minWidth: 50,
										}}>
										SL (kg)
									</TableCell>
									<TableCell
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
											minWidth: 50,
										}}>
										Nhập
									</TableCell>
									<TableCell
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
											minWidth: 50,
										}}>
										Xử lý
									</TableCell>
									<TableCell
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
											minWidth: 50,
										}}>
										Giao
									</TableCell>
									<TableCell
										sx={{
											p: 0,
											color: '#fff',
											borderRight: '1px solid #000',
											minWidth: 100,
										}}>
										Đơn nhanh
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								<TableRow>
									<TableCell
										sx={{
											padding: '5px 0',
											borderRight: '1px solid #000',
										}}>
										<b>{currentUser?.name}</b>
									</TableCell>
									<TableCell
										sx={{
											padding: '5px 0',
											borderRight: '1px solid #000',
										}}>
										{statistics.revenue?.toLocaleString()}
									</TableCell>
									<TableCell
										sx={{
											padding: '5px 0',
											borderRight: '1px solid #000',
											minWidth: 50,
										}}>
										{statistics?.transferOrders}
									</TableCell>
									<TableCell
										sx={{
											padding: '5px 0',
											borderRight: '1px solid #000',
											minWidth: 100,
										}}>
										{statistics.transferOrdersRevenue?.toLocaleString()}
									</TableCell>
									<TableCell
										sx={{
											padding: '5px 0',
											borderRight: '1px solid #000',
											minWidth: 50,
										}}>
										{statistics?.appOrders}
									</TableCell>
									<TableCell
										sx={{
											padding: '5px 0',
											borderRight: '1px solid #000',
											minWidth: 100,
										}}>
										{statistics.appOrdersRevenue?.toLocaleString()}
									</TableCell>
									<TableCell
										sx={{
											padding: '5px 0',
											borderRight: '1px solid #000',
											minWidth: 50,
										}}>
										{statistics?.ordersShip?.length - statistics?.transferOrders - statistics?.appOrders}
									</TableCell>
									<TableCell
										sx={{
											padding: '5px 0',
											borderRight: '1px solid #000',
											minWidth: 100,
										}}>
										{(
											statistics?.revenue -
											statistics?.transferOrdersRevenue -
											statistics?.appOrdersRevenue
										)?.toLocaleString()}
									</TableCell>
									<TableCell sx={{ padding: '5px 0', borderRight: '1px solid #000' }}>{statistics?.quantity}</TableCell>
									<TableCell sx={{ padding: '5px 0', borderRight: '1px solid #000' }}>
										{statistics.ordersCreate?.length}
									</TableCell>
									<TableCell sx={{ padding: '5px 0', borderRight: '1px solid #000' }}>
										{statistics.ordersProduce?.length}
									</TableCell>
									<TableCell sx={{ padding: '5px 0', borderRight: '1px solid #000' }}>
										{statistics.ordersShip?.length}
									</TableCell>
									<TableCell sx={{ padding: '5px 0', borderRight: '1px solid #000' }}>
										{statistics?.quickPurchaseOrders}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					)}
					{loadingStatistics && canSeeStore ? (
						<Box display='flex' justifyContent='center' sx={{ mb: 3 }}>
							<CircularProgress sx={{ color: mainGreen }} />
						</Box>
					) : (
						canSeeStore && (
							<Table sx={{ mb: 3, minWidth: 700, background: '#fff' }}>
								<TableHead sx={{ background: mainGreen, border: 'none' }}>
									<TableRow>
										<TableCell
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
												minWidth: 100,
											}}>
											<PersonIcon viewBox='0 -3 24 24' />
										</TableCell>
										<TableCell
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
												minWidth: 100,
											}}>
											Doanh thu (đ)
										</TableCell>
										<TableCell
											colSpan='2'
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
												minWidth: 150,
											}}>
											Đơn CK (đ)
										</TableCell>
										<TableCell
											colSpan='2'
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
												minWidth: 150,
											}}>
											Đơn App (đ)
										</TableCell>
										<TableCell
											colSpan='2'
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
												minWidth: 150,
											}}>
											Đơn TM (đ)
										</TableCell>
										<TableCell
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
												minWidth: 50,
											}}>
											SL (kg)
										</TableCell>
										<TableCell
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
												minWidth: 50,
											}}>
											Nhập
										</TableCell>
										<TableCell
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
												minWidth: 50,
											}}>
											Xử lý
										</TableCell>
										<TableCell
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
												minWidth: 50,
											}}>
											Giao
										</TableCell>
										<TableCell
											sx={{
												p: 0,
												color: '#fff',
												borderRight: '1px solid #000',
												minWidth: 100,
											}}>
											Đơn nhanh
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{userStatistics?.map((user, index) => {
										return (
											<TableRow key={index}>
												<TableCell
													sx={{
														padding: '5px 0',
														borderRight: '1px solid #000',
													}}>
													<b>{user.name}</b>
												</TableCell>
												<TableCell
													sx={{
														padding: '5px 0',
														borderRight: '1px solid #000',
													}}>
													{user.revenue?.toLocaleString()}
												</TableCell>
												<TableCell
													sx={{
														padding: '5px 0',
														borderRight: '1px solid #000',
														minWidth: 50,
													}}>
													{user?.transferOrders}
												</TableCell>
												<TableCell
													sx={{
														padding: '5px 0',
														borderRight: '1px solid #000',
														minWidth: 100,
													}}>
													{user?.transferOrdersRevenue?.toLocaleString()}
												</TableCell>
												<TableCell
													sx={{
														padding: '5px 0',
														borderRight: '1px solid #000',
														minWidth: 50,
													}}>
													{user?.appOrders}
												</TableCell>
												<TableCell
													sx={{
														padding: '5px 0',
														borderRight: '1px solid #000',
														minWidth: 100,
													}}>
													{user?.appOrdersRevenue?.toLocaleString()}
												</TableCell>
												<TableCell
													sx={{
														padding: '5px 0',
														borderRight: '1px solid #000',
														minWidth: 50,
													}}>
													{user?.ordersShip - user?.transferOrders - user?.appOrders}
												</TableCell>
												<TableCell
													sx={{
														padding: '5px 0',
														borderRight: '1px solid #000',
														minWidth: 100,
													}}>
													{(user?.revenue - user?.transferOrdersRevenue - user?.appOrdersRevenue)?.toLocaleString()}
												</TableCell>
												<TableCell
													sx={{
														padding: '5px 0',
														borderRight: '1px solid #000',
													}}>
													{user.quantity}
												</TableCell>
												<TableCell
													sx={{
														padding: '5px 0',
														borderRight: '1px solid #000',
													}}>
													{user.ordersCreate}
												</TableCell>
												<TableCell
													sx={{
														padding: '5px 0',
														borderRight: '1px solid #000',
													}}>
													{user.ordersProduce}
												</TableCell>
												<TableCell
													sx={{
														padding: '5px 0',
														borderRight: '1px solid #000',
													}}>
													{user.ordersShip}
												</TableCell>
												<TableCell
													sx={{
														padding: '5px 0',
														borderRight: '1px solid #000',
													}}>
													{user.quickPurchaseOrders}
												</TableCell>
											</TableRow>
										)
									})}
									<TableRow sx={{ background: mainRed, minHeight: '80px' }}>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											<b>TỔNG</b>
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											<b>{userStatistics?.reduce((sum, user) => (sum += user.revenue), 0)?.toLocaleString()}</b>
										</TableCell>
										<TableCell
											sx={{
												padding: '5px',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											<b>{userStatistics?.reduce((sum, user) => (sum += user.transferOrders), 0)}</b>
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											<b>
												{userStatistics
													?.reduce((sum, user) => (sum += user.transferOrdersRevenue), 0)
													?.toLocaleString()}
											</b>
										</TableCell>
										<TableCell
											sx={{
												padding: '5px',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											<b>{userStatistics?.reduce((sum, user) => (sum += user.appOrders), 0)}</b>
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											<b>
												{userStatistics?.reduce((sum, user) => (sum += user.appOrdersRevenue), 0)?.toLocaleString()}
											</b>
										</TableCell>
										<TableCell
											sx={{
												padding: '5px',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											<b>
												{userStatistics?.reduce(
													(sum, user) => (sum += user.ordersShip - user.transferOrders - user.appOrders),
													0,
												)}
											</b>
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											<b>
												{userStatistics
													?.reduce(
														(sum, user) => (sum += user.revenue - user.transferOrdersRevenue - user.appOrdersRevenue),
														0,
													)
													?.toLocaleString()}
											</b>
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											<b>{userStatistics?.reduce((sum, user) => (sum += +user.quantity), 0).toFixed(1)}</b>
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											<b>{userStatistics?.reduce((sum, user) => (sum += user.ordersCreate), 0)}</b>
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											<b>{userStatistics?.reduce((sum, user) => (sum += user.ordersProduce), 0)}</b>
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											<b>{userStatistics?.reduce((sum, user) => (sum += user.ordersShip), 0)}</b>
										</TableCell>
										<TableCell
											sx={{
												padding: '5px 0',
												borderRight: '1px solid #000',
												color: '#fff',
											}}>
											<b>{userStatistics?.reduce((sum, user) => (sum += user.quickPurchaseOrders), 0)}</b>
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						)
					)}
				</Box>
			</Box>
		</UserLayout>
	)
}
